import { CommonModule, registerLocaleData, ViewportScroller } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CurrentTimeZoneExtractorClass } from '@zaoblako/shared/modules/dates/classes/current-time-zone-extractor.class';
import { CurrentTimeZoneService } from '@zaoblako/web/core/services/current-time-zone.service';
import { ValidationService } from '@zaoblako/web/core/services/validation/validation.service';
import { RoutingService } from '@zaoblako/web/core/services/routing.service';
import { subscribeRouterScrollRestoration } from '@zaoblako/web/core/utils/router-scroll-restoration.util';
import { CommonLayoutComponent } from '@zaoblako/web/shared/layouts/common.layout.component';
import 'moment/locale/ru';
import moment from 'moment-timezone';
import { PrimeNGConfig } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ScrollTopModule } from 'primeng/scrolltop';
import { ToastModule } from 'primeng/toast';
import QuillNamespace from 'quill';
import MagicUrl from 'quill-magic-url';
import { Subscription } from 'rxjs';

registerLocaleData(localeRu, 'ru');
moment.locale('ru');
(QuillNamespace as any).register('modules/magicUrl', MagicUrl);

@Component({
  selector: 'crm-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ToastModule,
    ConfirmDialogModule,
    RouterModule,
    ScrollTopModule,
    CommonLayoutComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements AfterViewInit, OnInit, OnDestroy {
  private routerScrollSubscription: Subscription | null = null;

  public constructor(
    private readonly config: PrimeNGConfig,
    private readonly routingService: RoutingService,
    private readonly renderer: Renderer2,
    private readonly currentTimeZoneService: CurrentTimeZoneService,
    private readonly validationService: ValidationService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller
  ) {}

  public ngAfterViewInit() {
    const preLoading = this.renderer.selectRootElement('#preLoading');
    this.renderer.setStyle(preLoading, 'display', 'none');
  }

  public ngOnDestroy(): void {
    this.routerScrollSubscription?.unsubscribe();
    this.routerScrollSubscription = null;
  }

  public ngOnInit() {
    this.routerScrollSubscription = subscribeRouterScrollRestoration(this.router, this.viewportScroller);

    this.config.setTranslation({
      startsWith: 'Начинается с',
      contains: 'Содержит',
      notContains: 'Не содержит',
      endsWith: 'Оканчивается на',
      equals: 'Равно',
      notEquals: 'Не равно',
      noFilter: 'Нет фильтра',
      lt: 'Меньше чем',
      lte: 'Меньше или равно',
      gt: 'Больше чем',
      gte: 'Больше или равно',
      is: 'Равно',
      isNot: 'Не равно',
      before: 'Перед',
      after: 'После',
      dateIs: 'Дата',
      dateIsNot: 'Дата не',
      dateBefore: 'Дата перед',
      dateAfter: 'Дата после',
      apply: 'Применить',
      matchAll: 'Все совпадения',
      matchAny: 'Любое совпадение',
      addRule: 'Добавить правило',
      removeRule: 'Удалить правило',
      accept: 'Да',
      reject: 'Нет',
      choose: 'Выбрать',
      upload: 'Загрузить',
      cancel: 'Отменить',
      weekHeader: 'Неделя',
      weak: 'Слабый',
      medium: 'Средний',
      strong: 'Сильный',
      passwordPrompt: 'Введите пароль',
      emptyMessage: '',
      emptyFilterMessage: '',
      dayNames: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
      dayNamesShort: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
      dayNamesMin: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
      monthNames: [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь'
      ],
      monthNamesShort: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
      today: 'Сегодня',
      clear: 'Очистить'
    });

    // Need to date transformation
    CurrentTimeZoneExtractorClass.getInstance().setExtractor(() => this.currentTimeZoneService.getTimeZone());
    this.validationService.init();

    this.routingService.subscribeRoutes();
  }
}
